import React, { useContext, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useGetAffiliateOptions, useUserRolePermissions } from 'hooks';
import * as R from 'ramda';
import { format, sub } from 'date-fns';
import { useLazyGetReconsignmentsQuery } from 'feature/inventory-management/reconsignments/reconsignmentsEndpoints';
import { useLazyGetAutoReconsignmentsQuery } from 'feature/inventory-management/reconsignments/autoReconsignmentsEndpoints';
import { AuthContext } from 'authentication/AuthContext';
import { useNavigate } from 'react-router-dom';
import usePersistentFilters, {
  dateParser,
  filtersCounter,
  makeFilterParser,
} from 'hooks/usePersistentFilters';
import MobileNav from './mobile-nav/MobileNav';
import Filters from './filters/Filters';
import MainPanel from './main-panel/MainPanel';
import MobileFilters from './filters/MobileFilters';
import ManualDrawer from './edit/ManualDrawer';
import AutoDrawer from './edit/AutoDrawer';

const Reconsignments = () => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { currentAffiliate } = useUserRolePermissions();
  const { setCurrentSelectedAffiliate } = useContext(AuthContext);
  const affiliateOptions = useGetAffiliateOptions(['supplier']);
  const navigate = useNavigate();

  const [tab, setTab] = useState('manual');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [resultsPerPage, setResultsPerPage] = useState(25);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [reconsignments, setReconsignements] = useState([]);
  const [autoReconsignments, setAutoReconsignements] = useState([]);

  const [selectedReconsignment, setSelectedReconsignment] = useState(null);
  const [confirmDialogType, setConfirmDialogType] = useState('');
  const [modalMode, setModalMode] = useState('');
  const [copiedReconsignment, setCopiedReconsignment] = useState(null);

  const isCurrentInOptions = useMemo(
    () =>
      affiliateOptions.some(
        affiliate =>
          affiliate.customerIdName === currentAffiliate?.customerIdName &&
          affiliate.dataCompanyId === currentAffiliate?.dataCompanyId
      ),
    [currentAffiliate, affiliateOptions]
  );

  useEffect(() => {
    if (R.not(isCurrentInOptions)) {
      setCurrentSelectedAffiliate(affiliateOptions[0]);
      navigate(
        `/affiliate/${affiliateOptions[0].customerIdName}/${affiliateOptions[0].dataCompanyId}/inventory-management/reconsignments`,
        {
          replace: true,
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliateOptions, isCurrentInOptions]);

  const params = {
    inventoryOwner: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
    page: pageNumber,
    results: resultsPerPage,
  };

  const [
    triggerManual,
    { isFetching: isLoadingReconsignments = false, data: reconsignmentsData = {} },
  ] = useLazyGetReconsignmentsQuery();

  useMemo(() => {
    if (R.not(R.isEmpty(reconsignmentsData))) {
      const { currentPage, reconsignments: data, totalNumberOfResults } = reconsignmentsData;

      R.equals('manual', tab) && setPageNumber(currentPage);
      R.equals('manual', tab) && setTotalResults(totalNumberOfResults);
      setReconsignements(data);
    }
  }, [reconsignmentsData, tab]);

  const [
    triggerAuto,
    { data: autoReconsignmentsData = {}, isFetching: isLoadingAutoReconsignments = false },
  ] = useLazyGetAutoReconsignmentsQuery();

  // for auto only
  const handleCopyReconsignment = id => {
    const result = autoReconsignmentsData.autoReconsignments.find(
      n => n.autoReconsignmentId === id
    );
    setSelectedReconsignment(id);
    setCopiedReconsignment(result);
  };

  useMemo(() => {
    if (R.not(R.isEmpty(autoReconsignmentsData))) {
      const {
        currentPage,
        autoReconsignments: data,
        totalNumberOfResults,
      } = autoReconsignmentsData;

      R.equals('auto', tab) && setPageNumber(currentPage);
      R.equals('auto', tab) && setTotalResults(totalNumberOfResults);
      setAutoReconsignements(data);
    }
  }, [autoReconsignmentsData, tab]);

  const initialState = {
    fromLocation: null,
    inventoryOwner: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
    origin: null,
    product: null,
    reconsignmentId: '',
    status: 'ALL',
    toLocation: null,
    transferEndDate: format(new Date(), 'yyyy-MM-dd'),
    transferStartDate: format(sub(new Date(), { days: 31 }), 'yyyy-MM-dd'),
  };

  const parser = makeFilterParser({
    transferEndDate: dateParser,
    transferStartDate: dateParser,
  });

  const ignoreKeysForCounter = {
    auto: ['inventoryOwner', 'transferEndDate', 'transferStartDate'],
    manual: ['inventoryOwner'],
  };

  const [filters, setFilters, deferTilSearchIsClicked] = usePersistentFilters(
    'reconsignments',
    {
      ...initialState,
    },
    parser
  );

  const paramFilters = {
    autoReconsignmentId: R.prop('reconsignmentId', filters),
    endDate: R.prop('transferEndDate', filters),
    fromLocationIdName: R.path(['fromLocation', 'idName'], filters),
    originIdName: R.path(['origin', 'idName'], filters),
    productIdName: R.path(['product', 'idName'], filters),
    reconsignmentId: R.prop('reconsignmentId', filters),
    startDate: R.prop('transferStartDate', filters),
    status: R.prop('status', filters),
    toLocationIdName: R.path(['toLocation', 'idName'], filters),
  };

  // Clear out values from paramFilters obj to return only valid ones
  const definedValues = Object.keys(paramFilters)
    .filter(
      key =>
        paramFilters[key] !== null && paramFilters[key] !== '' && paramFilters[key] !== undefined
    )
    .reduce((acc, key) => ({ ...acc, [key]: paramFilters[key] }), {});

  useEffect(() => {
    if (R.not(R.isNil(currentAffiliate))) {
      triggerManual({ params: { ...params, ...definedValues } });
      triggerAuto({ params: { ...params, ...definedValues } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAffiliate, pageNumber, resultsPerPage]);

  return (
    <>
      {!lgUp && <MobileNav setIsFiltersOpen={setIsFiltersOpen} />}
      <Box
        sx={{
          mb: 2,
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          mt: { xs: 14, lg: 6 },
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          overflow: 'hidden',
          px: { lg: 3, xs: 2 },
        }}
      >
        <MainPanel
          autoReconsignments={autoReconsignments}
          confirmDialogType={confirmDialogType}
          definedValues={definedValues}
          filterCount={filtersCounter(initialState, filters, ignoreKeysForCounter[tab], parser)}
          handleCopyReconsignment={handleCopyReconsignment}
          isFiltersOpen={isFiltersOpen}
          loading={isLoadingReconsignments || isLoadingAutoReconsignments}
          modalMode={modalMode}
          pageNumber={pageNumber}
          reconsignments={reconsignments}
          resultsPerPage={resultsPerPage}
          selectedReconsignment={selectedReconsignment}
          setConfirmDialogType={setConfirmDialogType}
          setIsFiltersOpen={setIsFiltersOpen}
          setModalMode={setModalMode}
          setPageNumber={setPageNumber}
          setResultsPerPage={setResultsPerPage}
          setSelectedReconsignment={setSelectedReconsignment}
          setTab={setTab}
          tab={tab}
          totalResults={totalResults}
        />
        {isFiltersOpen && (
          <Drawer
            anchor={lgUp ? 'left' : 'right'}
            onClose={() => setIsFiltersOpen(false)}
            open={isFiltersOpen}
            slotProps={{
              paper: {
                sx: {
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  borderBottomLeftRadius: 5,
                  borderTopLeftRadius: 5,
                  overflowX: 'hidden',
                  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                  width: { xs: '100%', lg: 380 },
                },
              },
            }}
          >
            {!lgUp && (
              <MobileFilters
                definedValues={definedValues}
                filters={filters}
                getParams={params}
                initialState={initialState}
                isFiltersOpen={isFiltersOpen}
                persistFilters={deferTilSearchIsClicked}
                setFilters={setFilters}
                setIsFiltersOpen={setIsFiltersOpen}
                setPageNumber={setPageNumber}
                tab={tab}
                triggerAuto={triggerAuto}
                triggerManual={triggerManual}
              />
            )}
            {lgUp && (
              <Filters
                definedValues={definedValues}
                filters={filters}
                getParams={params}
                initialState={initialState}
                isFiltersOpen={isFiltersOpen}
                persistFilters={deferTilSearchIsClicked}
                setFilters={setFilters}
                setIsFiltersOpen={setIsFiltersOpen}
                setPageNumber={setPageNumber}
                tab={tab}
                triggerAuto={triggerAuto}
                triggerManual={triggerManual}
              />
            )}
          </Drawer>
        )}
        {R.and(R.not(R.isNil(selectedReconsignment)), R.isEmpty(confirmDialogType)) &&
          R.equals('manual', tab) && (
            <ManualDrawer
              copiedReconsignment={copiedReconsignment}
              modalMode={modalMode}
              selectedReconsignment={selectedReconsignment}
              setCopiedReconsignment={setCopiedReconsignment}
              setSelectedReconsignment={setSelectedReconsignment}
            />
          )}
        {R.and(R.not(R.isNil(selectedReconsignment)), R.isEmpty(confirmDialogType)) &&
          R.equals('auto', tab) && (
            <AutoDrawer
              autoReconsignmentsData={autoReconsignmentsData}
              copiedReconsignment={copiedReconsignment}
              loading={isLoadingAutoReconsignments}
              modalMode={modalMode}
              selectedReconsignment={selectedReconsignment}
              setCopiedReconsignment={setCopiedReconsignment}
              setSelectedReconsignment={setSelectedReconsignment}
            />
          )}
      </Box>
    </>
  );
};

export default Reconsignments;
